import { useMemo } from 'react';
import vitrafixRaw from '../data/vitrafix.json';
import BrandCatalog from '../components/catalog/BrandCatalog';
import type { CatalogItem, BrandConfig } from '../components/catalog/BrandCatalog';
import { useSEO, localKeywords } from '@/hooks/useSEO';
import { useStructuredData } from '@/hooks/useStructuredData';
import { buildBrandCatalogSchema, buildBreadcrumbSchema } from '@/lib/seo/schemas';

interface VitrafixRaw {
  id: string;
  title: string;
  category: string;
  sku?: string;
  images?: string[];
  description?: string;
  specs?: Record<string, string>;
  features?: string[];
  tags?: string[];
}

/* ══════════════════════════════════════════════════════════════
   VİTRAFİX CATEGORY TAXONOMY
   ══════════════════════════════════════════════════════════════ */
const CATEGORY_MAP: Record<string, { main: string; sub: string }> = {
  'Yapıştırma Harçları':          { main: 'Yapıştırma Harçları',               sub: 'Yapıştırma Harçları' },
  'Derz Dolgu Malzemeleri':       { main: 'Derz Dolgu Malzemeleri',             sub: 'Derz Dolgu Malzemeleri' },
  'Astar ve Katkılar':            { main: 'Performans Arttırıcı Astarlar',      sub: 'Astar ve Katkılar' },
  'Yüzey Temizlik ve Bakım':      { main: 'Yüzey Temizlik ve Bakım',            sub: 'Temizlik ve Bakım' },
  'Su Yalıtım Malzemeleri':       { main: 'Su Yalıtım Malzemeleri',             sub: 'Su Yalıtım Malzemeleri' },
  'Zemin Grubu':                  { main: 'Zemin Grubu',                        sub: 'Zemin Grubu' },
  'Silikon ve Mastikler':         { main: 'Silikon ve Mastikler',               sub: 'Silikon ve Mastikler' },
  'Tamir Harçları':               { main: 'Tamir Harçları ve Restorasyon',      sub: 'Tamir Harçları' },
};

/* ── Alt-kategori (ürün bazlı indirim) ── */
function resolveSubCat(item: VitrafixRaw): string {
  const t = item.title.toUpperCase();
  const cat = item.category;

  if (cat === 'Yapıştırma Harçları') {
    if (t.includes('POOL'))   return 'Havuz Yapıştırıcıları';
    if (t.includes('RAPID') || t.includes('QR')) return 'Hızlı Priz Alan Harçlar';
    if (t.includes('ULTRA') || t.includes('PLUS')) return 'Yüksek Performanslı';
    if (t.includes('PORS') || t.includes('FLEXY')) return 'Porselen Yapıştırıcılar';
    if (t.includes('PURLAM') || t.includes('HP')) return 'Hazır ve PU Yapıştırıcılar';
    return 'Standart Yapıştırıcılar';
  }
  if (cat === 'Derz Dolgu Malzemeleri') {
    if (t.includes('EPOXY')) return 'Epoksi Derz';
    if (t.includes('POOL'))  return 'Havuz Derz';
    if (t.includes('RUSTİK')) return 'Dekoratif Derz';
    return 'Çimento Esaslı Derz';
  }
  if (cat === 'Su Yalıtım Malzemeleri') {
    if (t.includes('HYDROSTOP')) return 'Tek Bileşenli Yalıtım';
    if (t.includes('PROOF'))     return 'İki Bileşenli Yalıtım';
    if (t.includes('PAH'))       return 'Detay ve Bant';
    return 'Su Yalıtımı';
  }
  return CATEGORY_MAP[cat]?.sub || cat;
}

function mapToItem(p: VitrafixRaw): CatalogItem {
  const mapping = CATEGORY_MAP[p.category] || { main: p.category, sub: p.category };
  const sub = resolveSubCat(p);

  return {
    id: p.id,
    title: p.title,
    sku: p.sku || '',
    images: p.images || [],
    groupKey: mapping.main,
    groupLabel: mapping.main,
    subtitle: sub !== mapping.main ? sub : undefined,
    tags: p.tags || [],
    chipFilters: {
      'Kategori': mapping.main,
      'Alt Kategori': sub,
    },
    specs: p.specs,
    features: p.features || [],
    description: p.description || '',
  };
}

const vitrafixBrand: BrandConfig = {
  brandName: 'VitraFix',
  tagline: 'İnşaat Kimyasalları | Seramik Yapıştırma Sistemleri',
  accentColor: '#1b5e20',
  accentLight: '#f1f8e9',
  accentMid: '#c8e6c9',
  groupMode: 'flat',
  searchPlaceholder: 'Ürün adı veya sınıf ara… (örn: C2TE, havuz, epoksi)',
  filterGroups: [
    { label: 'Kategori',     field: 'Kategori' },
    { label: 'Alt Kategori', field: 'Alt Kategori' },
  ],
};

export default function Vitrafix() {
  const items = useMemo<CatalogItem[]>(
    () => (vitrafixRaw as unknown as VitrafixRaw[]).map(mapToItem),
    []
  );

  useSEO({
    title: 'Vitra Fix Yapı Kimyasalları Diyarbakır | Seramik Yapıştırıcı — Mayi Mimarlık',
    description:
      "Diyarbakır Vitra Fix ana bayi Mayi Mimarlık. C2TE yapıştırıcı harç, epoksi derz, su yalıtım sistemleri. Kayapınar showroom — toplu proje teklifi.",
    keywords: localKeywords(
      'vitrafix diyarbakır',
      'seramik yapıştırıcı c2te diyarbakır',
      'epoksi derz diyarbakır',
      'yapı kimyasalları bayi diyarbakır',
      'vitra fix diyarbakır'
    ),
    canonical: 'https://mayimimarlik.com/vitrafix',
  });

  const schema = useMemo(() => {
    const catalog = buildBrandCatalogSchema({
      brandName: 'Vitra Fix',
      description: 'Vitra Fix yapı kimyasalları — Diyarbakır ana bayi kataloğu',
      url: 'https://mayimimarlik.com/vitrafix',
      productCount: items.length,
    });
    const breadcrumb = buildBreadcrumbSchema([
      { name: 'Ana Sayfa', url: 'https://mayimimarlik.com/' },
      { name: 'Vitra Fix', url: 'https://mayimimarlik.com/vitrafix' },
    ]);
    const { '@context': _a, ...cat } = catalog;
    const { '@context': _b, ...crumb } = breadcrumb;
    return { '@context': 'https://schema.org', '@graph': [cat, crumb] };
  }, [items.length]);

  useStructuredData('schema-vitrafix', schema);

  return <BrandCatalog config={vitrafixBrand} items={items} />;
}
